<template>
  <div>
    <IconTooltip
      v-if="iconClass || iconComponent"
      :iconClass="[$style.statusIcon, iconClass, $style[iconColorClass]]"
      :iconComponent="iconComponent"
      :tooltipContainerClass="$style.tooltipContainerInItemTable"
    >
      <MarkdownBlock
        v-if="tipTemplate"
        :mdTemplate="tipTemplate"
      />
    </IconTooltip>
  </div>
</template>

<script>
import * as Constants from 'client/util/Constants';
import { ApplicationStatusTooltipTexts, EOL, EOS } from 'client/util/Constants/ApplicationConstants';
import { MarkdownBlock } from '@jumpcloud/ui-components';
import { extend } from 'lodash';
import { mapGetters } from 'vuex';
import CriticalIcon from 'public/img/icons/critical.svg?inline';
import DateUtil from 'client/util/DateUtil';
import IconTooltip from 'client/components/Tooltip/IconTooltip.vue';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import NotAvailableIcon from 'public/img/icons/not-available.svg?inline';
import PausedStatusIcon from 'public/img/icons/pausedStatus.svg?inline';
import UserStateMixin from 'components/mixins/UserStateMixin';
import WarningIcon from 'public/img/icons/warning.svg?inline';
import moment from 'moment';

const {
  AdBridgeAgentStates,
  AdBridgeAgentTypes,
  BodyComponentConsts,
  ItemTypeConsts,
  PolicyStatusConsts,
  StoreModuleMap,
} = Constants;

const {
  android, iOS, iPadOS, tvOS,
} = Constants.default;
const { statusIconTypes } = BodyComponentConsts;
const { pending, success, uninstallPending } = PolicyStatusConsts;

const getterByType = {
  [statusIconTypes.adBridgeAgent]: 'getAdBridgeAgentStatusIcon',
  [statusIconTypes.application]: 'getApplicationStatusIcon',
  [statusIconTypes.policyStatus]: 'getPolicyStatusStatusIcon',
  [statusIconTypes.system]: 'getSystemStatusIcon',
  [statusIconTypes.systemMfa]: 'getSystemMFAStatusIcon',
  [statusIconTypes.systemUser]: 'getSystemUserStatusIcon',
  [statusIconTypes.user]: 'getUserStatusIcon',
};

/**
 * @todo
 * 1. Move getterByType to a method/computed prop named getStatusIcon() to
 *    perform the statusIconType to method mapping in a testable manner.
 * 2. Set iconColorClass using style properties rather than strings. e.g.:
 *    Use { iconColorClass: this.$style.active } instead of { iconColorClass: 'active' }
 * 3. Use SVG icons instead of icon fonts!
 */
export default {
  name: 'StatusIcon',

  components: {
    IconTooltip,
    MarkdownBlock,
  },

  mixins: [UserStateMixin],

  props: {
    item: {
      type: Object,
      required: true,
    },
    type: {
      type: String,
      required: true,
      validator: function validator(value) {
        return !!statusIconTypes[value];
      },
    },
  },

  computed: {
    ...mapGetters(StoreModuleMap[ItemTypeConsts.adBridgeAgent], ['isContactOverdue']),
    ...mapGetters(StoreModuleMap[ItemTypeConsts.application], [
      'isCertDateExpired',
      'getApplicationStatus',
    ]),

    iconClass() { return this[this.statusIconMethod]().iconClass; },
    iconColorClass() { return this[this.statusIconMethod]().iconColorClass || false; },
    iconComponent() { return this[this.statusIconMethod]().iconComponent; },
    statusIconMethod() { return getterByType[this.type]; },
    tipTemplate() { return this[this.statusIconMethod]().tipTemplate || false; },

    useImg() {
      return this.usePauseImg || this.useWarningImg;
    },
  },

  beforeCreate() {
    extend(
      this.$options.computed,
      mapGetters('SystemUserCollection', {
        getDisplayName: 'getDisplayName',
      }),
    );
  },

  methods: {
    /**
     * @todo The FormattedDate component wasn't appropriate to use here because
     * tipTemplates are strings which rendered in a slot passed to the IconTooltip.
     * If would be better if we had format utility library which could be used
     * to isolate logic like that seen here and reused in components like the
     * FormattedDate.
     */
    formatLastContact(lastContact) {
      return lastContact ? moment(lastContact).utc().fromNow() : Constants.default.dataNotAvailable;
    },

    getAdBridgeAgentStatusIcon() {
      return this.item.type === AdBridgeAgentTypes.importAgent
        ? this.getAdBridgeImportAgentStatusIcon()
        : this.getAdBridgeSyncAgentStatusIcon();
    },

    getAdBridgeImportAgentStatusIcon() {
      const lastContact2 = this.formatLastContact(this.item.lastContact);

      let iconClass;
      let iconColorClass;
      let iconComponent;
      let tipTemplateMesg;

      if (this.item.state === AdBridgeAgentStates.active) {
        if (this.isContactOverdue && this.isContactOverdue(this.item.id)) {
          iconComponent = WarningIcon;
          tipTemplateMesg = 'This agent is not currently reporting.';

          if (this.item.clusterSize <= 1) {
            iconComponent = CriticalIcon;
            tipTemplateMesg = `${tipTemplateMesg} This is the only import agent for this domain.`;
          }
        } else {
          iconClass = 'jc-check-circle';
          iconColorClass = 'active';
          tipTemplateMesg = 'This agent is actively importing.';
        }
      } else {
        iconComponent = PausedStatusIcon;
        tipTemplateMesg = 'This agent is currently suspended.';
      }

      return {
        iconClass,
        iconColorClass,
        iconComponent,
        tipTemplate: `${tipTemplateMesg}\n\n**Last Contact:** ${lastContact2}.`,
      };
    },

    getAdBridgeSyncAgentStatusIcon() {
      let iconClass;
      let iconColorClass;
      let iconComponent;
      let lastContact2 = this.formatLastContact(this.item.lastContact);
      let tipTemplateMesg;

      if (this.item.state === AdBridgeAgentStates.inactive) {
        iconComponent = WarningIcon;
        tipTemplateMesg = 'This agent is not currently reporting.';

        if (this.item.clusterSize <= 1) {
          iconComponent = CriticalIcon;
          tipTemplateMesg = `${tipTemplateMesg} This is the only sync agent for this domain.`;
        }
      } else if (this.item.state === AdBridgeAgentStates.unsealed) {
        iconClass = 'jc-actions-pend';
        iconColorClass = 'warning';
        lastContact2 = '';
        tipTemplateMesg = 'This agent has not yet been installed.';
      } else {
        iconClass = 'jc-check-circle';
        iconColorClass = 'active';
        tipTemplateMesg = 'This agent is actively syncing.';
      }

      return {
        iconClass,
        iconColorClass,
        iconComponent,
        tipTemplate: `${tipTemplateMesg}${lastContact2 ? `\n\n**Last Contact:** ${lastContact2}.` : ''}`,
      };
    },

    getApplicationStatusIcon() {
      const iconClass = 'jc-actions-check';
      const iconClassAlert = 'jc-actions-alert';
      const iconClassWarning = 'jc-actions-warning';
      const iconColorClass = 'active';
      const { status } = this.item;
      const { certExpired, idpCertExpirationAt } = this.item.sso;
      const isActive = this.item.active || this.item.sso.active;
      const isBookmark = this.item.name === 'bookmark' || this.item.name === '1Password';
      const expiredCertDate = DateUtil.hasExpired(idpCertExpirationAt);
      const certAboutToExpire = !expiredCertDate && DateUtil.daysUntil(idpCertExpirationAt) <= 61;

      if (isBookmark && isActive) {
        return {
          iconClass,
          iconColorClass,
          tipTemplate: ApplicationStatusTooltipTexts.bookmarkActive,
        };
      }

      if (isBookmark && !isActive) {
        return {
          iconComponent: PausedStatusIcon,
          tipTemplate: ApplicationStatusTooltipTexts.bookmarkDeactivated,
        };
      }

      if ([EOL, EOS].includes(status)) {
        const { appEOLorEOS } = KbArticleUrls;
        const learnMore = `[Learn more.](${appEOLorEOS})`;

        if (status === EOS) {
          if (certExpired) {
            return {
              iconClass: iconClassAlert,
              iconColorClass: 'inactive',
              tipTemplate: ApplicationStatusTooltipTexts.idpCertExpired,
            };
          }

          return {
            iconClass: iconClassAlert,
            iconColorClass: 'warning',
            tipTemplate: `${ApplicationStatusTooltipTexts.EOS} ${learnMore}`,
          };
        }

        return {
          iconClass: iconClassAlert,
          iconColorClass: 'inactive',
          tipTemplate: `${ApplicationStatusTooltipTexts.EOL} ${learnMore}`,
        };
      }

      if (certExpired || expiredCertDate) {
        return {
          iconClass: iconClassAlert,
          iconColorClass: 'inactive',
          tipTemplate: ApplicationStatusTooltipTexts.idpCertExpired,
        };
      }

      if (!!idpCertExpirationAt && certAboutToExpire) {
        return {
          iconClass: iconClassWarning,
          iconColorClass: 'warning',
          tipTemplate: ApplicationStatusTooltipTexts.idpCertExpiresSoon,
        };
      }

      if (isActive) {
        return {
          iconClass,
          iconColorClass,
          tipTemplate: ApplicationStatusTooltipTexts.active,
        };
      }

      return {
        iconComponent: PausedStatusIcon,
        tipTemplate: ApplicationStatusTooltipTexts.deactivated,
      };
    },

    getMobileStatusIcon() {
      const iconComponent = NotAvailableIcon;
      return {
        iconComponent,
        tipTemplate: `The system ${this.item.displayName} is not available and is not reporting. Check the device for more details.`,
      };
    },

    getPolicyStatusStatusIcon() {
      if (this.item.state === success) {
        return {
          iconClass: 'jc-actions-check',
          iconColorClass: 'active',
        };
      }

      if (this.item.state === pending || this.item.state === uninstallPending) {
        return {
          iconClass: 'jc-actions-check',
          iconColorClass: 'enforced',
          tipTemplate: this.item.detail,
        };
      }

      return {
        iconClass: 'jc-actions-alert',
        iconColorClass: 'inactive',
        tipTemplate: this.item.stdErr,
      };
    },

    getSystemMFAStatusIcon() {
      if (this.item.allowMultiFactorAuthentication) {
        return {
          iconClass: 'jc-mfa-active-up',
          iconColorClass: 'active',
        };
      }
      return { iconClass: '' };
    },

    getSystemStatusIcon() {
      if (this.item.os === iOS
        || this.item.os === iPadOS
        || this.item.os === tvOS
        || this.item.os === android) {
        return this.getMobileStatusIcon();
      }
      if (this.item.active) {
        return {
          iconClass: 'jc-actions-check',
          iconColorClass: 'active',
        };
      }
      return {
        iconClass: 'jc-actions-alert',
        iconColorClass: 'inactive',
        tipTemplate: `The system ${this.item.displayName} is not currently reporting and has been marked as inactive.`,
      };
    },

    getSystemUserStatusIcon() {
      const username = this.getDisplayName(this.item);

      if (!this.item.activated) {
        return {
          iconClass: 'jc-actions-pend',
          iconColorClass: 'pending',
          tipTemplate: `The user **${username}** has not completed the activation process.`,
        };
      }

      if (this.item.activated && this.item.account_locked) {
        return {
          iconComponent: CriticalIcon,
          tipTemplate: `The user account for **${username}** has been locked due to repeated failed login attempts.`,
        };
      }

      if (this.item.activated && this.item.password_expired) {
        return {
          iconComponent: CriticalIcon,
          tipTemplate: `The password for user **${username}** has expired.`,
        };
      }
      return {
        iconClass: 'jc-actions-check',
        iconColorClass: 'active',
      };
    },

    getUserStatusIcon() {
      const username = this.getDisplayName(this.item) || this.item.email;

      if (this.item.suspended) {
        return {
          iconComponent: PausedStatusIcon,
          tipTemplate: `The administrator **${username}** has been suspended, blocking access to the Multi-Tenant Portal.`,
        };
      }

      if (!this.item.registered) {
        return {
          iconClass: 'jc-actions-pend',
          iconColorClass: 'pending',
          tipTemplate: `The administrator **${username}** has not completed the registration process.`,
        };
      }

      return {
        iconClass: 'jc-actions-check',
        iconColorClass: 'active',
      };
    },

  },
};
</script>

<style module>
@import "../../../css/tooltips.css";

@value (enforcedOrange, errorRed, jcGreen, jcOrange) from '~css/colors.css';

.active {
  color: jcGreen;
}

.enforced {
  color: enforcedOrange;
}

.inactive,
.critical {
  color: errorRed;
}

.pending,
.warning {
  color: jcOrange;
}

.statusIcon {
  font-size: 18px;
  vertical-align: middle;
}

</style>
